/**
 * M27 Step 3 — Customize Policies (PR 4).
 *
 * The wow moment. Statutory baseline is ALWAYS active (lock #4);
 * customer PDFs layer on top via M19 ingestion. Conflicts with
 * statutory floor are flagged for HR's per-conflict decision; the
 * override-must-be-more-generous rule (lock #4) is enforced
 * client-side here AND server-side via the M19 promoter (M19.7).
 *
 * Three paths (lock #4 again):
 *   A. Use Saudi Labor Law defaults only — skip upload entirely.
 *   B. Upload customer policy PDFs — full M19 extraction.
 *   C. Combined — defaults baseline + selective uploads.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  FileUp,
  Globe,
  Sparkles,
  Upload,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { AIHelpsHereCallout } from "../../components/onboarding/AIHelpsHereCallout";
import {
  PolicyConflictCard,
  type ConflictDecision,
} from "../../components/onboarding/PolicyConflictCard";
import { api, ApiError } from "../../lib/api";
import { useLegalEntityGate } from "../../lib/legalEntity";
import { ONBOARDING_JOURNEY_QUERY_KEY } from "../../lib/onboarding";
import {
  countApprovedUnpromotedInDomains,
  detectStatutoryConflicts,
  isCombinedContinueBlocked,
  POLICY_OVERRIDE_DOMAINS,
  type PolicyOverrideDomainId,
} from "../../lib/onboarding-policy-conflicts";
import { useOnboardingAutoSave } from "../../lib/use-onboarding-autosave";
import { StepLayout } from "./StepLayout";

type PolicyPath = "defaults" | "upload" | "combined";

export function Step3PoliciesReal() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const auto = useOnboardingAutoSave("policies");
  const { legalEntityId: le, gate } = useLegalEntityGate();

  const [path, setPath] = useState<PolicyPath | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [cleanupBlocked, setCleanupBlocked] = useState<
    Array<{ domain: string; code: string; reason: string }>
  >([]);

  // Conflict decisions live in onboarding-state.steps.policies.data
  // so they survive reloads + can be reviewed later (commandment 28).
  const [decisions, setDecisions] = useState<
    Record<string, ConflictDecision>
  >({});
  const [overrideDomains, setOverrideDomains] = useState<
    PolicyOverrideDomainId[]
  >([]);
  const [promoteErr, setPromoteErr] = useState<string | null>(null);

  const stateQ = useQuery({
    queryKey: ONBOARDING_JOURNEY_QUERY_KEY,
    queryFn: () => api.getOnboardingJourneyState(),
  });
  useEffect(() => {
    const stepData = stateQ.data?.steps?.policies?.data ?? {};
    if (stepData.path && !path) setPath(stepData.path as PolicyPath);
    if (
      stepData.conflict_decisions &&
      typeof stepData.conflict_decisions === "object"
    ) {
      setDecisions(
        stepData.conflict_decisions as Record<string, ConflictDecision>,
      );
    }
    if (Array.isArray(stepData.override_domains)) {
      setOverrideDomains(stepData.override_domains as PolicyOverrideDomainId[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateQ.data]);

  const draftsQ = useQuery({
    queryKey: ["policy-drafts", le],
    queryFn: () => api.listPolicyDrafts(le),
    enabled: !!le,
    refetchInterval: (q) => {
      // Poll while any draft is still extracting.
      const data = q.state.data ?? [];
      const inFlight = data.some(
        (d) =>
          d.status === "pending" ||
          d.status === "extracting" ||
          d.status === "reviewing",
      );
      return inFlight ? 5000 : false;
    },
  });

  const upload = useMutation({
    mutationFn: () => api.uploadPolicyPdf(le, uploadFile!, "hr"),
    onSuccess: () => {
      setUploadFile(null);
      setUploadErr(null);
      qc.invalidateQueries({ queryKey: ["policy-drafts", le] });
    },
    onError: (e) => {
      setUploadErr(e instanceof ApiError ? e.detail : String(e));
    },
  });

  // Seed-defaults: fires on first selection of the "defaults" path,
  // and also on a "Re-run defaults" click for tenants whose first
  // seed run was incomplete. Idempotent on the server side.
  const seedDefaults = useMutation({
    mutationFn: () => api.seedKsaDefaults(),
    onSuccess: (result) => {
      auto.save({ defaults_seeded_at: new Date().toISOString(), defaults_result: result });
      qc.invalidateQueries({ queryKey: ["grades"] });
      qc.invalidateQueries({ queryKey: ["leave-types"] });
      qc.invalidateQueries({ queryKey: ["loan-products"] });
      qc.invalidateQueries({ queryKey: ["allowances"] });
    },
  });

  const prepareUpload = useMutation({
    mutationFn: () => api.prepareUploadPolicyPath(),
    onSuccess: (result) => {
      setCleanupBlocked(result.blocked ?? []);
      qc.invalidateQueries({ queryKey: ["grades"] });
      qc.invalidateQueries({ queryKey: ["leave-types"] });
      qc.invalidateQueries({ queryKey: ["loan-products"] });
      qc.invalidateQueries({ queryKey: ["allowances"] });
    },
  });

  const latestDraftId = useMemo(() => {
    const drafts = draftsQ.data ?? [];
    const active = drafts.find(
      (d) =>
        d.status === "reviewing" ||
        d.status === "approved" ||
        d.status === "partial",
    );
    return active?.id ?? drafts[0]?.id ?? null;
  }, [draftsQ.data]);

  const draftDetailQ = useQuery({
    queryKey: ["policy-draft-detail", latestDraftId],
    queryFn: () => api.getPolicyDraft(latestDraftId!),
    enabled:
      !!latestDraftId && (path === "combined" || path === "upload"),
  });

  const draftDiffQ = useQuery({
    queryKey: ["policy-draft-diff", latestDraftId],
    queryFn: () => api.getPolicyDraftDiff(latestDraftId!),
    enabled: !!latestDraftId && path === "combined",
  });

  const promotionStatusQ = useQuery({
    queryKey: ["policy-promotion-status"],
    queryFn: () => api.getPolicyPromotionStatus(),
    enabled: path === "upload" || path === "combined",
    refetchInterval:
      path === "upload" || path === "combined" ? 10000 : false,
  });

  const promoteSelected = useMutation({
    mutationFn: () =>
      api.promotePolicyDraft(latestDraftId!, {
        actor: "hr",
        domains:
          overrideDomains.length > 0 ? [...overrideDomains] : undefined,
      }),
    onSuccess: (summary) => {
      setPromoteErr(null);
      if ((summary.failed ?? 0) > 0 && summary.errors?.length) {
        setPromoteErr(summary.errors.map((e) => e.message).join("; "));
      }
      qc.invalidateQueries({ queryKey: ["policy-promotion-status"] });
      qc.invalidateQueries({ queryKey: ["policy-draft-detail", latestDraftId] });
      qc.invalidateQueries({ queryKey: ["policy-drafts", le] });
      qc.invalidateQueries({ queryKey: ["leave-types"] });
      qc.invalidateQueries({ queryKey: ["allowances"] });
      qc.invalidateQueries({ queryKey: ["grades"] });
      qc.invalidateQueries({ queryKey: ["loan-products"] });
    },
    onError: (e) => {
      setPromoteErr(e instanceof ApiError ? e.detail : String(e));
    },
  });

  function toggleOverrideDomain(domain: PolicyOverrideDomainId) {
    const next = overrideDomains.includes(domain)
      ? overrideDomains.filter((d) => d !== domain)
      : [...overrideDomains, domain];
    setOverrideDomains(next);
    auto.save({ override_domains: next });
  }

  function pickPath(next: PolicyPath) {
    setPath(next);
    auto.save({ path: next });
    if (next === "upload") {
      prepareUpload.mutate();
    } else {
      setCleanupBlocked([]);
      if (next === "defaults") {
        setOverrideDomains([]);
        auto.save({ override_domains: [] });
      }
      seedDefaults.mutate();
    }
  }

  function decide(key: string, dec: ConflictDecision) {
    const merged = { ...decisions, [key]: dec };
    setDecisions(merged);
    auto.save({ conflict_decisions: merged });
  }

  const conflicts = useMemo(
    () => detectStatutoryConflicts(draftDetailQ.data),
    [draftDetailQ.data],
  );

  const defaultsReady = Boolean(
    stateQ.data?.steps?.policies?.data?.defaults_seeded_at ||
      seedDefaults.isSuccess,
  );

  const approvedUnpromoted = countApprovedUnpromotedInDomains(
    draftDetailQ.data?.items,
    overrideDomains,
  );

  const continueDisabled =
    !path ||
    (path === "upload" && !promotionStatusQ.data?.has_promoted_items) ||
    (path === "combined" &&
      isCombinedContinueBlocked({
        defaultsReady,
        conflicts,
        decisions,
        overrideDomains,
        draftCount: draftsQ.data?.length ?? 0,
        approvedUnpromoted,
      }));

  if (gate) return gate;

  return (
    <StepLayout
      slug="policies"
      title={t("app.onb_step_policies")}
      subtitle={t("app.s3_subtitle")}
      continueDisabled={continueDisabled}
      continueLabel={t("app.s3_continue_to_jobs")}
    >
      <div className="flex flex-col gap-5">
        <AIHelpsHereCallout
          message={t("app.s3_ai_msg")}
          detail={t("app.s3_ai_detail")}
        />

        <section className="rounded-xl border border-border-subtle bg-surface-default p-4">
          <header className="flex items-center gap-2">
            <Globe size={16} className="text-brand-blue" aria-hidden />
            <h2 className="text-sm font-semibold text-text-primary">
              {t("app.s3_jurisdiction")}
            </h2>
          </header>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full border border-brand bg-brand text-brand-fg px-2.5 py-1 text-xs font-medium">
              <CheckCircle2 size={10} aria-hidden /> {t("app.s3_ksa_label")}
            </span>
            {[
              { key: "uae", labelKey: "app.s3_jur_uae" },
              { key: "qatar", labelKey: "app.s3_jur_qatar" },
              { key: "egypt", labelKey: "app.s3_jur_egypt" },
              { key: "bahrain", labelKey: "app.s3_jur_bahrain" },
              { key: "kuwait", labelKey: "app.s3_jur_kuwait" },
              { key: "oman", labelKey: "app.s3_jur_oman" },
            ].map((j) => (
              <span
                key={j.key}
                className="inline-flex items-center gap-1 rounded-full border border-border-subtle bg-surface-sunken px-2.5 py-1 text-xs text-text-tertiary"
              >
                {t(j.labelKey)}{" "}
                <span className="text-text-tertiary">{t("app.s3_soon_suffix")}</span>
              </span>
            ))}
          </div>
        </section>

        <section className="flex flex-col gap-3">
          <header>
            <h2 className="text-sm font-semibold text-text-primary">
              {t("app.s3_path_title")}
            </h2>
            <p className="mt-1 text-xs text-text-secondary">
              {t("app.s3_path_hint")}
            </p>
          </header>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <PathCard
              kind="defaults"
              selected={path === "defaults"}
              onPick={pickPath}
              title={t("app.s3_path_defaults_title")}
              body={t("app.s3_path_defaults_body")}
            />
            <PathCard
              kind="upload"
              selected={path === "upload"}
              onPick={pickPath}
              title={t("app.s3_path_upload_title")}
              body={t("app.s3_path_upload_body")}
              icon={Upload}
            />
            <PathCard
              kind="combined"
              selected={path === "combined"}
              onPick={pickPath}
              title={t("app.s3_path_combined_title")}
              body={t("app.s3_path_combined_body")}
            />
          </div>
        </section>

        {(path === "upload" || path === "combined") && (
          <section className="rounded-xl border border-border-subtle bg-surface-sunken p-4">
            <h2 className="text-sm font-semibold text-text-primary">
              {path === "combined"
                ? t("app.s3_combined_checklist_title")
                : t("app.s3_upload_checklist_title")}
            </h2>
            <ol className="mt-2 list-decimal ps-5 text-xs text-text-secondary">
              {path === "combined" ? (
                <>
                  <li>{t("app.s3_combined_checklist_defaults")}</li>
                  <li>{t("app.s3_combined_checklist_select")}</li>
                  <li>{t("app.s3_upload_checklist_upload")}</li>
                  <li>{t("app.s3_upload_checklist_review")}</li>
                  <li>{t("app.s3_combined_checklist_promote")}</li>
                </>
              ) : (
                <>
                  <li>{t("app.s3_upload_checklist_upload")}</li>
                  <li>{t("app.s3_upload_checklist_review")}</li>
                  <li>{t("app.s3_upload_checklist_promote")}</li>
                </>
              )}
            </ol>
            {path === "upload" && !promotionStatusQ.data?.has_promoted_items && (
              <p className="mt-2 text-xs text-amber-800">
                {t("app.s3_upload_promote_required")}
              </p>
            )}
            {path === "combined" && !defaultsReady && (
              <p className="mt-2 text-xs text-amber-800">
                {t("app.s3_combined_defaults_required")}
              </p>
            )}
            {path === "combined" &&
              overrideDomains.length > 0 &&
              approvedUnpromoted > 0 && (
                <p className="mt-2 text-xs text-amber-800">
                  {t("app.s3_combined_promote_required", {
                    n: approvedUnpromoted,
                  })}
                </p>
              )}
            {promotionStatusQ.data?.has_promoted_items && path === "upload" && (
              <p className="mt-2 text-xs text-emerald-800">
                {t("app.s3_upload_promoted_count", {
                  n: promotionStatusQ.data.promoted_total,
                })}
              </p>
            )}
          </section>
        )}

        {path === "combined" && (
          <section className="rounded-xl border border-border-subtle bg-surface-default p-4">
            <header>
              <h2 className="text-sm font-semibold text-text-primary">
                {t("app.s3_combined_domains_title")}
              </h2>
              <p className="mt-1 text-xs text-text-secondary">
                {t("app.s3_combined_domains_body")}
              </p>
            </header>
            <div className="mt-3 flex flex-wrap gap-2">
              {POLICY_OVERRIDE_DOMAINS.map((d) => {
                const active = overrideDomains.includes(d.id);
                return (
                  <button
                    key={d.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleOverrideDomain(d.id)}
                    className={[
                      "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                      active
                        ? "border-brand bg-brand text-brand-fg"
                        : "border-border-subtle bg-surface-sunken text-text-secondary hover:border-brand/40",
                    ].join(" ")}
                  >
                    {t(d.labelKey)}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {(path === "defaults" || path === "combined") && (
          <SeededDefaultsPanel
            seedDefaults={seedDefaults}
            variant={path === "combined" ? "combined" : "defaults"}
          />
        )}

        {path === "combined" && draftDiffQ.data && overrideDomains.length > 0 && (
          <section className="rounded-xl border border-brand-blue/30 bg-surface-default p-4">
            <h2 className="text-sm font-semibold text-text-primary">
              {t("app.s3_combined_diff_title")}
            </h2>
            <p className="mt-1 text-xs text-text-secondary">
              {t("app.s3_combined_diff_body")}
            </p>
            <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {overrideDomains.map((domain) => {
                const counts = draftDiffQ.data.by_domain[domain];
                if (!counts) {
                  return (
                    <li
                      key={domain}
                      className="rounded border border-border-subtle bg-surface-sunken px-3 py-2 text-xs"
                    >
                      <span className="font-medium capitalize">
                        {domain.replaceAll("_", " ")}
                      </span>
                      <span className="text-text-tertiary">
                        {" "}
                        — {t("app.s3_combined_diff_none")}
                      </span>
                    </li>
                  );
                }
                return (
                  <li
                    key={domain}
                    className="rounded border border-border-subtle bg-surface-sunken px-3 py-2 text-xs"
                  >
                    <span className="font-medium capitalize">
                      {domain.replaceAll("_", " ")}
                    </span>
                    <span className="text-text-secondary">
                      {" "}
                      · {t("app.s3_combined_diff_changed", {
                        n: counts.changed ?? 0,
                      })}{" "}
                      · {t("app.s3_combined_diff_new", { n: counts.new ?? 0 })}
                    </span>
                  </li>
                );
              })}
            </ul>
            {latestDraftId && approvedUnpromoted > 0 && (
              <div className="mt-3 flex flex-col gap-2">
                <button
                  type="button"
                  disabled={promoteSelected.isPending}
                  onClick={() => promoteSelected.mutate()}
                  className="inline-flex w-fit items-center gap-1 rounded bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
                >
                  {promoteSelected.isPending
                    ? t("policy.draft_promoting")
                    : t("app.s3_combined_promote_selected", {
                        n: approvedUnpromoted,
                      })}
                </button>
                {promoteErr && (
                  <p className="rounded bg-rose-50 px-2 py-1 text-xs text-rose-700">
                    {promoteErr}
                  </p>
                )}
              </div>
            )}
          </section>
        )}

        {cleanupBlocked.length > 0 && (
          <section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-amber-900">
              {t("app.s3_upload_cleanup_blocked_title")}
            </p>
            <ul className="mt-2 list-disc ps-5 text-xs text-amber-900">
              {cleanupBlocked.slice(0, 5).map((b) => (
                <li key={`${b.domain}-${b.code}`}>
                  {b.domain} / {b.code}: {b.reason}
                </li>
              ))}
            </ul>
          </section>
        )}

        {(path === "upload" || path === "combined") && (
          <section className="flex flex-col gap-3 rounded-xl border border-border-subtle bg-surface-default p-5">
            <header className="flex items-center gap-2">
              <FileUp size={16} className="text-brand-blue" aria-hidden />
              <h2 className="text-sm font-semibold text-text-primary">
                {t("app.s3_upload_title")}
              </h2>
            </header>
            <label className="flex h-32 cursor-pointer flex-col items-center justify-center gap-1 rounded border-2 border-dashed border-border-default bg-surface-sunken text-sm text-text-secondary hover:border-brand">
              <FileUp size={24} aria-hidden />
              <span>{uploadFile?.name ?? t("app.s3_upload_placeholder")}</span>
              <input
                type="file"
                accept="application/pdf"
                className="sr-only"
                onChange={(e) => {
                  setUploadFile(e.target.files?.[0] ?? null);
                  setUploadErr(null);
                }}
              />
            </label>
            {uploadErr && (
              <p className="rounded bg-rose-50 px-2 py-1 text-xs text-rose-700">
                {uploadErr}
              </p>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                disabled={!uploadFile || upload.isPending}
                onClick={() => upload.mutate()}
                className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
              >
                {upload.isPending ? t("app.s3_extracting") : t("app.s3_upload_btn")}
              </button>
            </div>
            <p className="text-xs text-text-tertiary">
              {t("app.s3_drafts_land_in")}{" "}
              <Link
                to="/policies/ai-ingestion"
                className="text-brand hover:underline"
              >
                {t("app.s3_drafts_link")}
              </Link>{" "}
              {t("app.s3_drafts_after")}
            </p>
          </section>
        )}

        {(path === "upload" || path === "combined") &&
          draftsQ.data &&
          draftsQ.data.length > 0 && (
            <section className="rounded-xl border border-border-subtle bg-surface-default p-5">
              <header className="mb-2 flex items-center gap-2">
                <h2 className="text-sm font-semibold text-text-primary">
                  {t("app.s3_recent_extractions")}
                </h2>
                <span className="text-xs text-text-tertiary">
                  {t("app.s3_total_suffix", { n: draftsQ.data.length })}
                </span>
              </header>
              <ul className="flex flex-col gap-1.5">
                {draftsQ.data.slice(0, 5).map((d) => (
                  <li
                    key={d.id}
                    className="flex items-center gap-2 rounded border border-border-subtle bg-surface-sunken px-3 py-2 text-xs"
                  >
                    <span className="font-mono text-text-tertiary">
                      {d.id.slice(0, 8)}
                    </span>
                    <span className="rounded bg-surface-default px-1.5 py-0.5 text-text-secondary">
                      {d.status}
                    </span>
                    <span className="text-text-tertiary">
                      {t("app.s3_pages_domains", { pages: d.page_count, domains: d.extracted_domains?.length ?? 0 })}
                    </span>
                    <Link
                      to={`/policies/ai-ingestion/drafts/${d.id}`}
                      className="ms-auto text-brand hover:underline"
                    >
                      {t("app.s3_review_link")}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

        {conflicts.length > 0 && (path === "combined" || path === "upload") && (
          <section className="flex flex-col gap-3">
            <header className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-700" aria-hidden />
              <h2 className="text-sm font-semibold text-text-primary">
                {t("app.s3_conflicts_title")}
              </h2>
              <span className="text-xs text-text-tertiary">
                {t("app.s3_conflicts_resolved", {
                  resolved: conflicts.filter((c) => decisions[c.key] !== undefined).length,
                  total: conflicts.length,
                })}
              </span>
            </header>
            <p className="text-xs text-text-secondary">
              {t("app.s3_conflicts_body")}
            </p>
            <div className="flex flex-col gap-3">
              {conflicts.map((c) => (
                <PolicyConflictCard
                  key={c.key}
                  label={c.label}
                  customerValue={c.customerValue}
                  statutoryValue={c.statutoryValue}
                  articleRef={c.article}
                  overrideAllowed={c.overrideAllowed}
                  decision={decisions[c.key]}
                  onDecide={(dec) => decide(c.key, dec)}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </StepLayout>
  );
}

// ---------------------------------------------------------------------------
// Seeded defaults status (defaults-only + combined paths)
// ---------------------------------------------------------------------------

function SeededDefaultsPanel({
  seedDefaults,
  variant,
}: {
  seedDefaults: {
    isPending: boolean;
    isError: boolean;
    isSuccess: boolean;
    error: unknown;
    data?: Record<string, Record<string, number>>;
    mutate: () => void;
  };
  variant: "defaults" | "combined";
}) {
  const { t } = useTranslation();
  return (
    <section
      className={[
        "rounded-xl border p-4",
        variant === "combined"
          ? "border-brand-blue/30 bg-surface-default"
          : "border-emerald-200 bg-emerald-50",
      ].join(" ")}
    >
      <header className="flex items-center gap-2">
        <Sparkles
          size={16}
          className={variant === "combined" ? "text-brand-blue" : "text-brand-teal"}
          aria-hidden
        />
        <h2
          className={[
            "text-sm font-semibold",
            variant === "combined" ? "text-text-primary" : "text-emerald-900",
          ].join(" ")}
        >
          {seedDefaults.isPending
            ? t("app.s3_seeding")
            : seedDefaults.isError
              ? t("app.s3_seed_err")
              : variant === "combined"
                ? t("app.s3_combined_seeded_title")
                : t("app.s3_seeded")}
        </h2>
      </header>
      {seedDefaults.isError && (
        <p className="mt-1 text-xs text-rose-700">
          {(seedDefaults.error as Error).message}. {t("app.s3_seed_err_retry")}
        </p>
      )}
      {seedDefaults.data && (
        <ul
          className={[
            "mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3",
            variant === "combined" ? "text-text-secondary" : "text-emerald-800",
          ].join(" ")}
        >
          {Object.entries(seedDefaults.data)
            .filter(([domain]) => domain !== "reactivated")
            .map(([domain, r]) => {
            const inserted =
              Object.entries(r as Record<string, number>).find(([k]) =>
                k.endsWith("_inserted"),
              )?.[1] ?? 0;
            const reactivated =
              (
                seedDefaults.data?.reactivated as
                  | Record<string, number>
                  | undefined
              )?.[domain] ?? 0;
            return (
              <li key={domain} className="flex justify-between gap-2">
                <span className="capitalize">{domain.replaceAll("_", " ")}</span>
                <span className="font-semibold">
                  {(inserted as number) > 0
                    ? t("app.s3_inserted_new", { n: inserted })
                    : reactivated > 0
                      ? t("app.s3_reactivated_n", { n: reactivated })
                      : t("app.s3_already_present")}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <p
        className={[
          "mt-2 text-xs",
          variant === "combined" ? "text-text-secondary" : "text-emerald-800",
        ].join(" ")}
      >
        {variant === "combined"
          ? t("app.s3_combined_seeded_body")
          : t("app.s3_seeded_body")}
      </p>
      <button
        type="button"
        onClick={() => seedDefaults.mutate()}
        disabled={seedDefaults.isPending}
        className={[
          "mt-3 inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs disabled:opacity-60",
          variant === "combined"
            ? "border-border-subtle bg-surface-sunken text-text-primary hover:bg-surface-muted"
            : "border-emerald-300 bg-surface-default text-emerald-900 hover:bg-emerald-100",
        ].join(" ")}
      >
        {seedDefaults.isPending ? t("app.s3_rerunning") : t("app.s3_rerun_defaults")}
      </button>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Path card
// ---------------------------------------------------------------------------

function PathCard({
  kind,
  selected,
  onPick,
  title,
  body,
  icon: Icon = Sparkles,
}: {
  kind: PolicyPath;
  selected: boolean;
  onPick: (k: PolicyPath) => void;
  title: string;
  body: string;
  icon?: typeof Sparkles;
}) {
  return (
    <button
      type="button"
      onClick={() => onPick(kind)}
      aria-pressed={selected}
      className={[
        "relative flex flex-col gap-2 rounded-xl border p-4 text-start transition-shadow hover:shadow-sm",
        selected
          ? "border-brand bg-ai-bg ring-2 ring-brand/30"
          : "border-border-subtle bg-surface-default",
      ].join(" ")}
    >
      {selected && (
        <span
          aria-hidden
          className="absolute inset-x-0 top-0 h-0.5 rounded-t-xl bg-brand-gradient-h"
        />
      )}
      <header className="flex items-center gap-2">
        <span
          className={[
            "flex h-8 w-8 items-center justify-center rounded-lg",
            selected
              ? "bg-brand text-brand-fg"
              : "bg-surface-sunken text-text-secondary",
          ].join(" ")}
        >
          <Icon size={14} aria-hidden />
        </span>
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
        {selected && (
          <CheckCircle2
            size={14}
            aria-hidden
            className="ms-auto text-brand"
          />
        )}
      </header>
      <p className="text-xs text-text-secondary">{body}</p>
    </button>
  );
}
